import React, { useState } from 'react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

const CMS_LABELS = {
    wordpress:  { label: 'WP', color: '#2563eb', title: 'WordPress' },
    ssh_static: { label: 'SSH', color: '#16a34a', title: 'React / SSH Bridge' },
    react_api:  { label: 'API', color: '#9333ea', title: 'React API' },
};

const DomainList = ({ domains, onRefresh, onSelect, selectedDomain }) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [cmsType, setCmsType] = useState('wordpress');
    const [newUrl, setNewUrl] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newCustomApiUrl, setNewCustomApiUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddDomain = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const params = new URLSearchParams({
                url: newUrl,
                cms_type: cmsType,
                wp_username: newUsername,
                wp_app_password: newPassword,
                custom_api_url: newCustomApiUrl,
            });
            await apiService.makeRequest(`/organic-seo/domains?${params}`, { method: 'POST' });
            toast.success("Domain connected successfully!");
            setShowAddModal(false);
            setNewUrl(''); setNewUsername(''); setNewPassword(''); setNewCustomApiUrl('');
            setCmsType('wordpress');
            onRefresh();
        } catch (error) {
            toast.error(error.message || "Failed to connect domain");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteDomain = async (domainId, url) => {
        if (!window.confirm(`Are you sure you want to completely delete ${url}? All profiles and tasks will be lost.`)) return;
        try {
            await apiService.makeRequest(`/organic-seo/domains/${domainId}`, { method: 'DELETE' });
            toast.success("Domain deleted successfully.");
            onRefresh();
        } catch (error) {
            toast.error(error.message || "Failed to delete domain");
        }
    };

    const isSSH = cmsType === 'ssh_static';
    const isWP = cmsType === 'wordpress';

    return (
        <div className="domain-list-container widget-card">
            <div className="domain-list-header">
                <h3>Connected Sites</h3>
                <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+</button>
            </div>

            <ul className="domain-list">
                {domains.map(domain => {
                    const cms = CMS_LABELS[domain.cms_type] || CMS_LABELS.wordpress;
                    return (
                        <li
                            key={domain.id}
                            className={`domain-item ${selectedDomain?.id === domain.id ? 'active' : ''}`}
                            onClick={() => onSelect(domain)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                                <span style={{
                                    fontSize: '10px', fontWeight: 700, padding: '1px 5px',
                                    borderRadius: '4px', background: cms.color, color: '#fff',
                                    flexShrink: 0
                                }} title={cms.title}>{cms.label}</span>
                                <strong className="domain-name-text" title={domain.url} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {domain.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </strong>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <span className={`status-badge ${domain.status}`}>{domain.status}</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteDomain(domain.id, domain.url); }}
                                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 6px', fontSize: '14px', borderRadius: '4px' }}
                                    title="Delete Domain"
                                >✕</button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '480px' }}>
                        <h2>Add New Site</h2>
                        <form onSubmit={handleAddDomain}>
                            {/* CMS Type selector */}
                            <div className="form-group">
                                <label>Platform Type</label>
                                <select value={cmsType} onChange={e => setCmsType(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                                    <option value="wordpress">WordPress (REST API)</option>
                                    <option value="ssh_static">React / SSH Bridge (SCP files)</option>
                                    <option value="react_api">React (OS-Elfny Internal API)</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Site URL</label>
                                <input type="url" value={newUrl} onChange={e => setNewUrl(e.target.value)}
                                    placeholder="https://example.com" required />
                            </div>

                            {isWP && (
                                <>
                                    <div className="form-group">
                                        <label>WP Username</label>
                                        <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} required />
                                    </div>
                                    <div className="form-group">
                                        <label>WP Application Password</label>
                                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
                                    </div>
                                </>
                            )}

                            {isSSH && (
                                <>
                                    <div className="form-group">
                                        <label>SSH Username</label>
                                        <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)}
                                            placeholder="e.g. root or deploy" required />
                                    </div>
                                    <div className="form-group">
                                        <label>SSH Password</label>
                                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Server IP | Remote Path</label>
                                        <input type="text" value={newCustomApiUrl} onChange={e => setNewCustomApiUrl(e.target.value)}
                                            placeholder="212.227.237.71|/var/www/vhosts/site.com/httpdocs" required />
                                        <small style={{ color: '#6b7280', fontSize: '11px' }}>
                                            Format: <code>IP|/path/to/webroot</code>. Posts saved to /path/blog/slug/
                                        </small>
                                    </div>
                                </>
                            )}

                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? "Connecting..." : "Add Site"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DomainList;
